// ═══════════════════════════════════════════════════════════════════════
// ControlCentrePage — Step ⑧ · Phase D · Financial Control Centre
// ═══════════════════════════════════════════════════════════════════════
// Phase A shipped: framework tiles, engine version, period status, one
//    infrastructure heartbeat.
// Phase B shipped: 10 business reconciliation checks consuming aggregators.
// Phase C shipped: exception drill-down, Ack/Resolve/Archive lifecycle,
//    CSV exports, run-detail modal, top-3 drift on FAIL/WARN tiles.
// Phase D ships (this sprint):
//   • 30-day trend sparklines on every tile
//   • In-app notifications on state transitions (run + exception)
//   • Statutory audit-trail CSV (full lifecycle history)
//   • Final Health Score weighting (severity-weighted WARN + stale aging)
//
// Constitutional discipline preserved:
//   • This page consumes ONLY /api/control-centre/* endpoints.
//   • No operational data is ever mutated. Only integrity_exceptions
//     status transitions and integrity_notifications.seen flags mutate.
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import {
  ShieldCheck, CheckCircle2, AlertTriangle, AlertOctagon, Info,
  Play, RefreshCw, Clock, Activity, GitBranch, BookLock,
  X, Download, FileDown, ChevronRight, ArrowUpRight, Undo2,
  Bell, BellDot, TrendingUp, ScrollText,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { ResponsiveContainer, LineChart, Line, Tooltip as RTooltip, YAxis } from "recharts";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";

const SEVERITY_PILL = {
  critical:      "bg-red-50 text-red-800 border-red-200",
  high:          "bg-orange-50 text-orange-800 border-orange-200",
  medium:        "bg-amber-50 text-amber-800 border-amber-200",
  warn:          "bg-amber-50 text-amber-700 border-amber-200",
  informational: "bg-slate-50 text-slate-700 border-slate-200",
};

const STATUS_STYLE = {
  PASS: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  FAIL: { icon: AlertOctagon, color: "text-red-600",     bg: "bg-red-50",     border: "border-red-200" },
  WARN: { icon: AlertTriangle, color: "text-amber-600",  bg: "bg-amber-50",   border: "border-amber-200" },
  INFO: { icon: Info,         color: "text-slate-600",   bg: "bg-slate-50",   border: "border-slate-200" },
  "—":  { icon: Clock,        color: "text-slate-400",   bg: "bg-slate-50",   border: "border-slate-200" },
};

const EXC_STATUS_STYLE = {
  OPEN:         "bg-red-50 text-red-800 border-red-200",
  ACKNOWLEDGED: "bg-amber-50 text-amber-800 border-amber-200",
  RESOLVED:     "bg-emerald-50 text-emerald-800 border-emerald-200",
  ARCHIVED:     "bg-slate-100 text-slate-600 border-slate-300",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}
function fmtNum(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

// ─────────────────────────────────────────────────────────────────────
// Sparkline — 30-day run trend on each tile
// ─────────────────────────────────────────────────────────────────────
function TrendSparkline({ checkKey }) {
  const [points, setPoints] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/control-centre/checks/${checkKey}/trend`, {
          params: { days: 30 },
        });
        if (cancelled) return;
        setPoints(r?.data?.points || []);
      } catch {
        setPoints([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [checkKey]);
  if (!loaded) {
    return (
      <div className="h-8 flex items-center justify-center text-[10px] text-slate-400">
        loading trend…
      </div>
    );
  }
  if (points.length < 2) {
    return (
      <div
        data-testid={`fcc-trend-empty-${checkKey}`}
        className="h-8 flex items-center justify-center text-[10px] text-slate-400"
      >
        {points.length === 1 ? "1 run · trend appears after 2+ runs" : "no runs in last 30 days"}
      </div>
    );
  }
  // Colour a point red when it's a FAIL / WARN run so operators see
  // where regressions happened at a glance.
  const data = points.map((p, i) => ({
    idx: i,
    delta: p.delta,
    abs: Math.abs(p.delta),
    status: p.status,
    when: p.computed_at,
  }));
  const stroke =
    data.some(d => d.status === "FAIL") ? "#dc2626" :
    data.some(d => d.status === "WARN") ? "#d97706" :
    "#16a34a";
  return (
    <div
      className="h-10 mt-2"
      data-testid={`fcc-trend-${checkKey}`}
      title={`${points.length} run(s) in the last 30 days`}
    >
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <YAxis hide domain={["auto", "auto"]} />
          <RTooltip
            wrapperStyle={{ fontSize: 10 }}
            formatter={(v, k) => k === "abs"
              ? [`₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`, "|Δ|"]
              : [v, k]}
            labelFormatter={(_, payload) => {
              if (!payload || !payload[0]) return "";
              return fmtDateTime(payload[0].payload.when) + ` · ${payload[0].payload.status}`;
            }}
          />
          <Line
            type="monotone" dataKey="abs" stroke={stroke}
            dot={{ r: 2 }} strokeWidth={1.5} isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Notification bell + panel
// ─────────────────────────────────────────────────────────────────────
function NotificationBell({ user }) {
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [nR, cR] = await Promise.all([
        api.get("/control-centre/notifications", { params: { limit: 30 } }),
        api.get("/control-centre/notifications/_unseen-count"),
      ]);
      setItems(Array.isArray(nR?.data) ? nR.data : []);
      setCount(cR?.data?.unseen ?? 0);
    } catch (e) {
      // Silent — bell is auxiliary.
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    // Refresh every 60s while the tab is visible.
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, [refresh]);

  const markOne = async (id) => {
    try {
      await api.patch(`/control-centre/notifications/${id}/seen`, {});
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark seen");
    }
  };
  const markAll = async () => {
    setBusy(true);
    try {
      await api.post("/control-centre/notifications/_mark-all-seen", {});
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark all seen");
    } finally { setBusy(false); }
  };

  const kindStyle = {
    run_state_change:     "bg-red-50 text-red-800 border-red-200",
    exception_lifecycle:  "bg-amber-50 text-amber-800 border-amber-200",
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="fcc-notif-bell"
        className="relative inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-300 text-slate-700 rounded hover:bg-slate-50"
        title="Notifications"
      >
        {count > 0 ? <BellDot size={14} className="text-red-600" /> : <Bell size={14} />}
        <span>Alerts</span>
        {count > 0 && (
          <span
            data-testid="fcc-notif-badge"
            className="ml-1 min-w-4 h-4 px-1 text-[10px] font-bold rounded-full bg-red-600 text-white flex items-center justify-center"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="fcc-notif-panel"
          className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] bg-white shadow-2xl border border-slate-200 rounded-lg z-30 flex flex-col"
        >
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="font-semibold text-slate-900">Notifications</div>
              <div className="text-[11px] text-slate-500">{count} unseen · state changes only</div>
            </div>
            <div className="flex items-center gap-2">
              {count > 0 && isAdmin && (
                <button
                  onClick={markAll}
                  disabled={busy}
                  data-testid="fcc-notif-mark-all"
                  className="text-xs text-emerald-700 hover:text-emerald-900"
                >Mark all seen</button>
              )}
              <button
                onClick={() => setOpen(false)}
                data-testid="fcc-notif-close"
                className="text-slate-500 hover:text-slate-900"
              ><X size={14} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div
                data-testid="fcc-notif-empty"
                className="p-6 text-center text-xs text-slate-500"
              >
                No notifications yet. State changes appear here as they happen.
              </div>
            ) : (
              items.map(n => (
                <div
                  key={n.id}
                  data-testid={`fcc-notif-item-${n.id}`}
                  className={`px-3 py-2 border-b border-slate-100 hover:bg-slate-50 ${n.seen ? "opacity-60" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase rounded border ${kindStyle[n.kind] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                        {n.kind === "run_state_change" ? "run" : "lifecycle"}
                      </span>
                      <span className="ml-2 text-[10px] font-mono text-slate-500">
                        {n.previous_state} → {n.new_state}
                      </span>
                      <div className="text-xs text-slate-800 mt-1 line-clamp-2">{n.message}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {fmtDateTime(n.created_at)}{n.triggered_by ? ` · ${n.triggered_by}` : ""}
                      </div>
                    </div>
                    {!n.seen && isAdmin && (
                      <button
                        onClick={() => markOne(n.id)}
                        data-testid={`fcc-notif-mark-${n.id}`}
                        className="text-[10px] text-slate-500 hover:text-slate-900"
                        title="Mark as seen"
                      >Seen</button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tiles — Health Score + Period Status
// ─────────────────────────────────────────────────────────────────────
function HealthScoreTile({ health }) {
  const score = health?.score ?? 100;
  const breakdown = health?.breakdown || [];
  const counts = { PASS: 0, FAIL: 0, WARN: 0, INFO: 0 };
  for (const b of breakdown) counts[b.status] = (counts[b.status] || 0) + 1;
  const colour =
    score >= 95 ? "text-emerald-600" :
    score >= 80 ? "text-amber-600" :
    "text-red-600";
  const ringColour =
    score >= 95 ? "ring-emerald-200 bg-emerald-50" :
    score >= 80 ? "ring-amber-200 bg-amber-50" :
    "ring-red-200 bg-red-50";
  return (
    <Card data-testid="fcc-health-score" className={`p-6 ring-1 ${ringColour}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Financial Health</div>
        <ShieldCheck size={18} className={colour} />
      </div>
      <div className="mt-4 flex items-baseline gap-2">
        <span data-testid="fcc-health-score-value" className={`text-5xl font-black tracking-tight ${colour}`}>
          {score}
        </span>
        <span className="text-slate-500 text-sm">/ 100</span>
      </div>
      <div className="mt-3 text-xs text-slate-600">
        {counts.PASS || 0} PASS · {counts.WARN || 0} WARN · {counts.FAIL || 0} FAIL · {counts.INFO || 0} INFO
      </div>
    </Card>
  );
}

function PeriodStatusTile({ period }) {
  const status = period?.current_status || "OPEN";
  const colour = status === "FROZEN" ? "text-emerald-700" : "text-amber-700";
  const bg = status === "FROZEN" ? "bg-emerald-50 ring-emerald-200" : "bg-amber-50 ring-amber-200";
  return (
    <Card data-testid="fcc-period-status" className={`p-6 ring-1 ${bg}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Current Financial Period</div>
        <BookLock size={18} className={colour} />
      </div>
      <div className="mt-4 flex items-baseline gap-3">
        <span className={`text-3xl font-black tracking-tight ${colour}`} data-testid="fcc-period-current">
          {period?.current_period || "—"}
        </span>
        <span className={`px-2 py-0.5 text-[11px] rounded border ${
          status === "FROZEN"
            ? "bg-white text-emerald-700 border-emerald-200"
            : "bg-white text-amber-700 border-amber-200"
        }`}>
          {status}
        </span>
      </div>
      <div className="mt-3 text-xs text-slate-600 space-y-0.5">
        {status === "FROZEN" ? (
          <>
            <div>Frozen by: <span className="font-mono">{period?.frozen_by || "—"}</span></div>
            <div>Frozen on: <span className="font-mono">{fmtDateTime(period?.frozen_at)}</span></div>
          </>
        ) : (
          <div className="text-slate-500 italic">
            Freeze workflow ships in a later sprint.
          </div>
        )}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Check Tile — now with Top-3 drift preview + click-to-drill
// ─────────────────────────────────────────────────────────────────────
function CheckTile({ tile, onOpen }) {
  const last = tile.last_run;
  const status = last?.status || "—";
  const style = STATUS_STYLE[status] || STATUS_STYLE["—"];
  const Icon = style.icon;
  const hasDrift = ["FAIL", "WARN"].includes(status);
  const canDrill = last?.id && hasDrift;
  return (
    <Card
      data-testid={`fcc-check-${tile.check_key}`}
      className={`p-5 ring-1 ${style.border} ${canDrill ? "cursor-pointer hover:ring-2 transition-all" : ""}`}
      onClick={canDrill ? () => onOpen(tile) : undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 truncate">{tile.check_key}</div>
          <div className="font-semibold text-slate-900 mt-1">{tile.label}</div>
        </div>
        <span
          data-testid={`fcc-check-severity-${tile.check_key}`}
          className={`px-2 py-0.5 text-[10px] rounded border uppercase shrink-0 ${SEVERITY_PILL[tile.severity] || SEVERITY_PILL.informational}`}
        >
          {tile.severity}
        </span>
      </div>
      <div className={`mt-4 flex items-center gap-2 ${style.color}`}>
        <Icon size={16} />
        <span className="font-bold">{status}</span>
        {last?.delta !== undefined && status !== "—" && (
          <span data-testid={`fcc-check-delta-${tile.check_key}`} className="text-xs text-slate-600 ml-2">
            Δ {fmtNum(last.delta)}
          </span>
        )}
        {tile.open_exception_count > 0 && (
          <span
            data-testid={`fcc-check-open-count-${tile.check_key}`}
            className="ml-auto text-[10px] font-mono bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-700"
          >
            {tile.open_exception_count} open
          </span>
        )}
      </div>
      <div className="mt-2 text-xs text-slate-500">
        {last ? <>Last run: {fmtDateTime(last.computed_at)}{last.triggered_by ? <> · by {last.triggered_by}</> : null}</> : "Never run"}
      </div>
      {last?.notes ? (
        <div className="mt-2 text-[11px] text-slate-600 bg-slate-50 px-2 py-1 border border-slate-200 line-clamp-2">
          {last.notes}
        </div>
      ) : null}
      {tile.top_drift?.length > 0 && (
        <div
          data-testid={`fcc-check-top-drift-${tile.check_key}`}
          className="mt-3 border-t border-slate-100 pt-2 space-y-1"
        >
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Top drift contributors
          </div>
          {tile.top_drift.map((d, i) => (
            <div
              key={d.id || i}
              className="flex items-center justify-between text-[11px] text-slate-700"
              title={d.entity_id}
            >
              <span className="truncate mr-2">{d.entity_label || d.entity_id || "(unlabelled)"}</span>
              <span className={`font-mono shrink-0 ${d.delta_contribution >= 0 ? "text-red-600" : "text-blue-600"}`}>
                {d.delta_contribution >= 0 ? "+" : ""}
                {fmtNum(d.delta_contribution)}
              </span>
            </div>
          ))}
        </div>
      )}
      {/* Phase D — 30-day sparkline */}
      <TrendSparkline checkKey={tile.check_key} />
      {canDrill && (
        <div className="mt-1 flex items-center justify-end text-[11px] text-slate-500">
          Click to drill down <ChevronRight size={12} className="ml-0.5" />
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Exception Drawer — full list + status filter + lifecycle actions
// ─────────────────────────────────────────────────────────────────────
function ExceptionDrawer({ open, tile, onClose, onRefresh, isAdmin }) {
  const runId = tile?.last_run?.id;
  const [status, setStatus] = useState("OPEN");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [actionExc, setActionExc] = useState(null);
  const [action, setAction] = useState(null);

  const {
    paged: pagedRows, page, pageSize, total,
    setPage, setPageSize,
  } = useClientPagination(rows, { initialPageSize: 25 });

  const load = useCallback(async () => {
    if (!open || !runId) return;
    setBusy(true);
    try {
      const r = await api.get(`/control-centre/runs/${runId}/exceptions`, {
        params: { status: status || undefined, limit: 200 },
      });
      setRows(Array.isArray(r?.data) ? r.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load exceptions");
    } finally {
      setBusy(false);
    }
  }, [open, runId, status]);

  useEffect(() => { load(); }, [load]);

  const csvHref = useMemo(() => {
    if (!runId) return null;
    const base = (import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const params = new URLSearchParams();
    params.set("check_key", tile?.check_key || "");
    if (status) params.set("status", status);
    return `${base}/api/control-centre/exceptions.csv?${params.toString()}`;
  }, [runId, tile, status]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60]" data-testid="fcc-exception-drawer">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[720px] bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{tile?.check_key}</div>
            <div className="font-semibold text-slate-900">{tile?.label}</div>
            <div className="text-xs text-slate-500 mt-1">
              Latest run · {fmtDateTime(tile?.last_run?.computed_at)} · period {tile?.last_run?.period_key}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="fcc-exc-drawer-close"
            className="text-slate-500 hover:text-slate-900 p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Filter + CSV bar */}
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
          {["OPEN", "ACKNOWLEDGED", "RESOLVED", "ARCHIVED", ""].map(s => (
            <button
              key={s || "all"}
              data-testid={`fcc-exc-filter-${s || "all"}`}
              onClick={() => setStatus(s)}
              className={`px-2.5 py-1 text-xs rounded border ${
                status === s
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
              }`}
            >
              {s || "All"}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={load}
              data-testid="fcc-exc-refresh"
              className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            >
              <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
            </button>
            <a
              href={csvHref || "#"}
              data-testid="fcc-exc-export-csv"
              className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
              target="_blank" rel="noreferrer"
            >
              <FileDown size={12} /> CSV
            </a>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="fcc-exc-empty">
              {busy ? "Loading…" : `No exceptions found for status "${status || "All"}"`}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10">
                <tr>
                  <th className="text-left py-2 px-3">Entity</th>
                  <th className="text-right py-2 px-3">Δ Contribution</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-left py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map(exc => {
                  const s = exc.status || "OPEN";
                  return (
                    <tr key={exc.id} className="border-t border-slate-100" data-testid={`fcc-exc-row-${exc.id}`}>
                      <td className="py-2 px-3 text-xs">
                        <div className="font-medium text-slate-900">{exc.entity_label || exc.entity_id}</div>
                        <div className="text-[10px] text-slate-500">{exc.entity_type} · {exc.entity_id}</div>
                      </td>
                      <td className={`py-2 px-3 text-xs text-right font-mono ${exc.delta_contribution >= 0 ? "text-red-700" : "text-blue-700"}`}>
                        {exc.delta_contribution >= 0 ? "+" : ""}{fmtNum(exc.delta_contribution)}
                      </td>
                      <td className="py-2 px-3 text-xs">
                        <span className={`px-2 py-0.5 rounded border text-[10px] ${EXC_STATUS_STYLE[s] || EXC_STATUS_STYLE.OPEN}`}>
                          {s}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-xs">
                        <div className="flex gap-1 flex-wrap">
                          {isAdmin && s === "OPEN" && (
                            <>
                              <button
                                onClick={() => { setActionExc(exc); setAction("acknowledge"); }}
                                data-testid={`fcc-exc-ack-${exc.id}`}
                                className="px-2 py-0.5 text-[10px] border border-amber-200 text-amber-800 rounded hover:bg-amber-50"
                              >Acknowledge</button>
                              <button
                                onClick={() => { setActionExc(exc); setAction("resolve"); }}
                                data-testid={`fcc-exc-resolve-${exc.id}`}
                                className="px-2 py-0.5 text-[10px] border border-emerald-200 text-emerald-800 rounded hover:bg-emerald-50"
                              >Resolve</button>
                            </>
                          )}
                          {isAdmin && s === "ACKNOWLEDGED" && (
                            <>
                              <button
                                onClick={() => { setActionExc(exc); setAction("resolve"); }}
                                data-testid={`fcc-exc-resolve-${exc.id}`}
                                className="px-2 py-0.5 text-[10px] border border-emerald-200 text-emerald-800 rounded hover:bg-emerald-50"
                              >Resolve</button>
                              <button
                                onClick={() => { setActionExc(exc); setAction("reopen"); }}
                                data-testid={`fcc-exc-reopen-${exc.id}`}
                                className="px-2 py-0.5 text-[10px] border border-slate-200 text-slate-700 rounded hover:bg-slate-50"
                              >Reopen</button>
                            </>
                          )}
                          {isAdmin && s === "RESOLVED" && (
                            <>
                              <button
                                onClick={() => { setActionExc(exc); setAction("archive"); }}
                                data-testid={`fcc-exc-archive-${exc.id}`}
                                className="px-2 py-0.5 text-[10px] border border-slate-300 text-slate-700 rounded hover:bg-slate-100"
                              >Archive</button>
                              <button
                                onClick={() => { setActionExc(exc); setAction("reopen"); }}
                                data-testid={`fcc-exc-reopen-${exc.id}`}
                                className="px-2 py-0.5 text-[10px] border border-slate-200 text-slate-700 rounded hover:bg-slate-50"
                              >Reopen</button>
                            </>
                          )}
                          {isAdmin && s === "ARCHIVED" && (
                            <button
                              onClick={() => { setActionExc(exc); setAction("reopen"); }}
                              data-testid={`fcc-exc-reopen-${exc.id}`}
                              className="px-2 py-0.5 text-[10px] border border-slate-200 text-slate-700 rounded hover:bg-slate-50 inline-flex items-center gap-1"
                            >
                              <Undo2 size={10} /> Restore
                            </button>
                          )}
                          {!isAdmin && (
                            <span className="text-[10px] text-slate-400 italic">admin only</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {rows.length > 0 && (
          <div className="px-4 py-2 border-t border-slate-200" data-testid="fcc-exc-pagination">
            <PaginationControls
              page={page} pageSize={pageSize} total={total}
              onPage={setPage} onPageSize={setPageSize}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </div>
        )}
      </div>

      {actionExc && action && (
        <LifecycleModal
          exception={actionExc}
          action={action}
          onCancel={() => { setActionExc(null); setAction(null); }}
          onDone={async () => {
            setActionExc(null); setAction(null);
            await load();
            if (onRefresh) onRefresh();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Lifecycle Modal — confirmation + optional reason
// ─────────────────────────────────────────────────────────────────────
const ACTION_META = {
  acknowledge: { label: "Acknowledge", verb: "acknowledged", tone: "amber",   need: false },
  resolve:     { label: "Resolve",     verb: "resolved",     tone: "emerald", need: true  },
  archive:     { label: "Archive",     verb: "archived",     tone: "slate",   need: false },
  reopen:      { label: "Reopen",      verb: "reopened",     tone: "slate",   need: true  },
};

function LifecycleModal({ exception, action, onCancel, onDone }) {
  const meta = ACTION_META[action];
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (meta.need && !reason.trim()) {
      toast.error("A reason is required for this action");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/control-centre/exceptions/${exception.id}`, {
        action, reason: reason.trim(),
      });
      toast.success(`Exception ${meta.verb}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${meta.label} failed`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4" data-testid="fcc-lifecycle-modal">
      <Card className="p-6 max-w-md w-full">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Exception Lifecycle</div>
            <div className="text-lg font-bold text-slate-900 mt-1">{meta.label}</div>
          </div>
          <button onClick={onCancel} className="text-slate-500 hover:text-slate-900" aria-label="Cancel" data-testid="fcc-lifecycle-cancel">
            <X size={18} />
          </button>
        </div>
        <div className="mt-3 text-sm text-slate-700">
          <div><span className="text-slate-500">Entity:</span> <span className="font-medium">{exception.entity_label || exception.entity_id}</span></div>
          <div className="mt-1"><span className="text-slate-500">Δ:</span> <span className="font-mono">{fmtNum(exception.delta_contribution)}</span></div>
          <div className="mt-1"><span className="text-slate-500">Current status:</span> <span className="font-mono">{exception.status}</span></div>
        </div>
        <div className="mt-4">
          <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">
            Reason {meta.need ? <span className="text-red-600">*</span> : <span className="text-slate-400">(optional)</span>}
          </label>
          <textarea
            data-testid="fcc-lifecycle-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={
              action === "resolve"
                ? "How was the underlying drift eliminated?"
                : action === "reopen"
                ? "Why is this exception being reopened?"
                : "Optional context…"
            }
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm border border-slate-300 rounded text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="fcc-lifecycle-submit"
            className={`px-3 py-1.5 text-sm font-semibold rounded text-white
              ${meta.tone === "amber" ? "bg-amber-600 hover:bg-amber-700"
                : meta.tone === "emerald" ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-slate-800 hover:bg-slate-900"}`}
          >
            {busy ? "Working…" : meta.label}
          </button>
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Run Detail Modal — click a run row to see its exception breakdown
// ─────────────────────────────────────────────────────────────────────
function RunDetailModal({ run, onClose }) {
  const [detail, setDetail] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const [dR, eR] = await Promise.all([
          api.get(`/control-centre/runs/${run.id}`),
          api.get(`/control-centre/runs/${run.id}/exceptions`, { params: { limit: 200 } }),
        ]);
        if (cancelled) return;
        setDetail(dR?.data || null);
        setRows(Array.isArray(eR?.data) ? eR.data : []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load run detail");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [run.id]);

  const style = STATUS_STYLE[run.status] || STATUS_STYLE["—"];
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="fcc-run-detail-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <Card className="relative w-full max-w-3xl bg-white max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              Run · {run.id.slice(0, 8)}…
            </div>
            <div className="font-semibold text-slate-900">{run.check_label || run.check_key}</div>
            <div className="text-xs text-slate-500 mt-1">
              {fmtDateTime(run.computed_at)} · period {run.period_key} · engine v{run.engine_version} · by {run.triggered_by}
            </div>
          </div>
          <button onClick={onClose} data-testid="fcc-run-detail-close" className="text-slate-500 hover:text-slate-900 p-1"><X size={18} /></button>
        </div>
        <div className="px-5 py-3 border-b border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Status</div>
            <div className={`font-bold ${style.color}`}>{run.status}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Δ</div>
            <div className="font-mono">{fmtNum(run.delta)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Tolerance</div>
            <div className="font-mono">{fmtNum(run.tolerance)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Duration</div>
            <div className="font-mono">{run.duration_ms || 0} ms</div>
          </div>
        </div>
        {detail?.exception_status_breakdown && (
          <div className="px-5 py-2 text-xs text-slate-600">
            OPEN {detail.exception_status_breakdown.OPEN || 0} · ACK {detail.exception_status_breakdown.ACKNOWLEDGED || 0}
            {" "}· RES {detail.exception_status_breakdown.RESOLVED || 0}
            {" "}· ARC {detail.exception_status_breakdown.ARCHIVED || 0}
            {" "}(total {detail.exception_count})
          </div>
        )}
        {run.notes && (
          <div className="px-5 py-2 text-xs text-slate-700 bg-slate-50 border-y border-slate-100 line-clamp-3">{run.notes}</div>
        )}
        <div className="flex-1 overflow-y-auto">
          {busy ? (
            <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="fcc-run-detail-empty">
              This run recorded no exceptions.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-2 px-3">Entity</th>
                  <th className="text-right py-2 px-3">Δ</th>
                  <th className="text-left py-2 px-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(exc => (
                  <tr key={exc.id} className="border-t border-slate-100">
                    <td className="py-2 px-3 text-xs">{exc.entity_label || exc.entity_id}</td>
                    <td className={`py-2 px-3 text-xs text-right font-mono ${exc.delta_contribution >= 0 ? "text-red-700" : "text-blue-700"}`}>
                      {fmtNum(exc.delta_contribution)}
                    </td>
                    <td className="py-2 px-3 text-xs">
                      <span className={`px-2 py-0.5 rounded border text-[10px] ${EXC_STATUS_STYLE[exc.status] || EXC_STATUS_STYLE.OPEN}`}>
                        {exc.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────
export default function ControlCentrePage() {
  const [summary, setSummary] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [user, setUser] = useState(null);
  const [drawerTile, setDrawerTile] = useState(null);
  const [runModal, setRunModal] = useState(null);

  const isAdmin = user?.role === "admin";

  const {
    paged: pagedRuns, page: runPage, pageSize: runPageSize, total: runTotal,
    setPage: setRunPage, setPageSize: setRunPageSize,
  } = useClientPagination(runs, { initialPageSize: 25 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sR, rR, meR] = await Promise.all([
        api.get("/control-centre/_summary"),
        api.get("/control-centre/runs", { params: { limit: 200 } }),
        api.get("/auth/me").catch(() => null),
      ]);
      setSummary(sR?.data || null);
      setRuns(Array.isArray(rR?.data) ? rR.data : []);
      if (meR?.data) setUser(meR.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to load Control Centre`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRunAll = async () => {
    if (!isAdmin) {
      toast.error("Admin role required to trigger a run");
      return;
    }
    setRunning(true);
    try {
      const res = await api.post("/control-centre/run", {});
      const count = (res?.data?.results || []).length;
      toast.success(`Ran ${count} check(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const runsCsvHref = useMemo(() => {
    const base = (import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/control-centre/runs.csv?limit=5000`;
  }, []);
  const excCsvHref = useMemo(() => {
    const base = (import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/control-centre/exceptions.csv?limit=5000`;
  }, []);
  const auditCsvHref = useMemo(() => {
    const base = (import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/control-centre/audit-trail.csv?limit=50000`;
  }, []);

  const engineVersion = summary?.engine_version?.version || "—";
  const engineNote = summary?.engine_version?.last_contract_summary || "";
  const engineDate = summary?.engine_version?.last_contract_change || "";

  return (
    <div className="px-6 py-6 max-w-screen-2xl mx-auto" data-testid="fcc-page">
      <Toaster position="top-right" />
      <PageHeader
        eyebrow="Step ⑧ · Phase D"
        title="Financial Control Centre"
        subtitle="Watchdog framework + 10 business reconciliation checks + exception drill-down + Ack/Resolve/Archive lifecycle + CSV exports + 30-day trend sparklines + state-change notifications + statutory audit-trail export."
        onRefresh={load}
        refreshing={loading}
        actions={
          <div className="flex items-center gap-2">
            <NotificationBell user={user} />
            <a
              href={auditCsvHref}
              target="_blank" rel="noreferrer"
              data-testid="fcc-audit-export-csv"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-emerald-300 text-emerald-800 bg-emerald-50 rounded hover:bg-emerald-100"
              title="Statutory audit-trail CSV (full lifecycle history)"
            >
              <ScrollText size={14} /> Audit CSV
            </a>
            <a
              href={runsCsvHref}
              target="_blank" rel="noreferrer"
              data-testid="fcc-runs-export-csv"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-300 text-slate-700 rounded hover:bg-slate-50"
              title="Download Run History as CSV"
            >
              <Download size={14} /> Runs CSV
            </a>
            <a
              href={excCsvHref}
              target="_blank" rel="noreferrer"
              data-testid="fcc-exc-global-export-csv"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-300 text-slate-700 rounded hover:bg-slate-50"
              title="Download Exceptions as CSV"
            >
              <Download size={14} /> Exceptions CSV
            </a>
            <button
              onClick={onRunAll}
              disabled={running || !isAdmin}
              data-testid="fcc-rerun-all"
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded ${
                isAdmin
                  ? "bg-slate-900 text-white hover:bg-slate-800"
                  : "bg-slate-200 text-slate-400 cursor-not-allowed"
              }`}
              title={isAdmin ? "Trigger a manual run" : "Admin role required"}
            >
              {running ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
              Run Control Centre
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <HealthScoreTile health={summary?.health} />
        <PeriodStatusTile period={summary?.period_status} />
      </div>

      <Card className="px-5 py-3 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-2 ring-1 ring-slate-200">
        <div className="flex items-center gap-3">
          <GitBranch size={16} className="text-slate-500" />
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wider">Financial Engine Version</span>
            <span
              data-testid="fcc-engine-version"
              className="ml-3 text-lg font-mono font-bold text-slate-900"
            >
              v{engineVersion}
            </span>
            <span className="ml-3 text-xs text-slate-500">
              · last contract change: {engineDate || "—"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-600">
          <Activity size={14} className="text-slate-500" />
          <span>
            Last run: {summary?.last_run_at ? fmtDateTime(summary.last_run_at) : "Never"}
            {summary?.last_run_triggered_by ? <> · by {summary.last_run_triggered_by}</> : null}
          </span>
        </div>
      </Card>

      {engineNote ? (
        <Card className="px-5 py-3 mb-6 ring-1 ring-slate-200 bg-slate-50">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Engine Contract Note</div>
          <div className="text-xs text-slate-700">{engineNote}</div>
        </Card>
      ) : null}

      <div className="mb-6">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900">Reconciliation Checks</h2>
          <span className="text-xs text-slate-500">
            {summary?.registered_check_count || 0} registered · Phase D
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fcc-checks-grid">
          {(summary?.checks || []).map(t => (
            <CheckTile key={t.check_key} tile={t} onOpen={setDrawerTile} />
          ))}
        </div>
      </div>

      <Card className="ring-1 ring-slate-200 overflow-hidden" data-testid="fcc-run-history">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">Run History</h2>
            <div className="text-xs text-slate-500">Click any row for detail</div>
          </div>
          <span className="text-xs text-slate-500">{runTotal} row(s)</span>
        </div>
        {runs.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-slate-500" data-testid="fcc-run-history-empty">
            No runs yet. Click <strong>Run Control Centre</strong> above to fire the first run.
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-2 px-3">Computed At</th>
                  <th className="text-left py-2 px-3">Check</th>
                  <th className="text-left py-2 px-3">Period</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-right py-2 px-3">Δ</th>
                  <th className="text-right py-2 px-3">Exc.</th>
                  <th className="text-left py-2 px-3">Engine</th>
                  <th className="text-left py-2 px-3">By</th>
                  <th className="text-left py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {pagedRuns.map(r => {
                  const st = STATUS_STYLE[r.status] || STATUS_STYLE["—"];
                  return (
                    <tr
                      key={r.id}
                      data-testid={`fcc-run-row-${r.id}`}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => setRunModal(r)}
                    >
                      <td className="py-2 px-3 text-xs text-slate-600 whitespace-nowrap">{fmtDateTime(r.computed_at)}</td>
                      <td className="py-2 px-3 text-xs">{r.check_label || r.check_key}</td>
                      <td className="py-2 px-3 text-xs">{r.period_key}</td>
                      <td className={`py-2 px-3 text-xs font-bold ${st.color}`}>{r.status}</td>
                      <td className="py-2 px-3 text-xs text-right">{fmtNum(r.delta)}</td>
                      <td className="py-2 px-3 text-xs text-right">{r.exception_count}</td>
                      <td className="py-2 px-3 text-[10px] font-mono text-slate-500">{r.engine_version}</td>
                      <td className="py-2 px-3 text-xs text-slate-600">{r.triggered_by}</td>
                      <td className="py-2 px-3 text-xs text-slate-400"><ArrowUpRight size={12} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-slate-200" data-testid="fcc-run-pagination">
            <PaginationControls
              page={runPage} pageSize={runPageSize} total={runTotal}
              onPage={setRunPage} onPageSize={setRunPageSize}
              pageSizeOptions={[10, 25, 50, 100, 200]}
            />
          </div>
          </>
        )}
      </Card>

      <ExceptionDrawer
        open={!!drawerTile}
        tile={drawerTile}
        onClose={() => setDrawerTile(null)}
        onRefresh={load}
        isAdmin={isAdmin}
      />
      {runModal && (
        <RunDetailModal run={runModal} onClose={() => setRunModal(null)} />
      )}
    </div>
  );
}
